import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { devLog } from '../../utils/logger';
import { passwordResetApi } from '../../services/passwordResetApi';

const ResetPasswordPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();

  // Validate token on component mount
  useEffect(() => {
    if (!token) {
      toast.error('Invalid reset link. Please request a new password reset.');
      navigate('/forgot-password');
      return;
    }

    const validateToken = async () => {
      try {
        devLog.debug('🔍 Validating reset token:', token);
        
        // Use the API service to validate token
        const result = await passwordResetApi.validateResetToken(token);
        
        if (result.valid) {
          setIsTokenValid(true);
        } else {
          setIsTokenValid(false);
          toast.error(result.message || 'Invalid or expired reset link. Please request a new password reset.');
        }
      } catch (error) {
        devLog.error('Token validation failed:', error);
        setIsTokenValid(false);
        toast.error('Failed to validate reset link. Please request a new password reset.');
      }
    };

    validateToken();
  }, [token, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!password || !confirmPassword) {
      toast.error('Please fill in all fields.');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      devLog.debug('🔒 Resetting password with token:', token);
      
      // Use the API service to reset password
      const result = await passwordResetApi.resetPassword(token!, password, confirmPassword);
      
      if (result.status === 'success') {
        setIsSuccess(true);
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      devLog.error('Password reset failed:', error);
      toast.error('Failed to reset password. Please try again or request a new reset link.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const handleRequestNewReset = () => {
    navigate('/forgot-password');
  };

  // Loading state while validating token
  if (isTokenValid === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[radial-gradient(circle_at_20%_20%,rgba(241,79,48,.12),transparent_55%),radial-gradient(circle_at_80%_0%,rgba(31,159,132,.12),transparent_50%),linear-gradient(160deg,#f8fafc,#eef2ff_45%,#e0e7ff_90%)] dark:bg-[radial-gradient(circle_at_20%_20%,rgba(241,79,48,.15),transparent_45%),radial-gradient(circle_at_80%_0%,rgba(31,159,132,.12),transparent_40%),linear-gradient(160deg,#070c17,#0f1727_40%,#111936_75%)] text-slate-900 dark:text-slate-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-400 mx-auto"></div>
          <p className="mt-4 text-lg font-medium text-slate-700 dark:text-slate-300">Validating reset link...</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (isTokenValid === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[radial-gradient(circle_at_20%_20%,rgba(241,79,48,.12),transparent_55%),radial-gradient(circle_at_80%_0%,rgba(31,159,132,.12),transparent_50%),linear-gradient(160deg,#f8fafc,#eef2ff_45%,#e0e7ff_90%)] dark:bg-[radial-gradient(circle_at_20%_20%,rgba(241,79,48,.15),transparent_45%),radial-gradient(circle_at_80%_0%,rgba(31,159,132,.12),transparent_40%),linear-gradient(160deg,#070c17,#0f1727_40%,#111936_75%)] text-slate-900 dark:text-slate-100">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white/95 dark:bg-midnight-900/95 backdrop-blur-xl rounded-2xl shadow-xl shadow-slate-200/60 dark:shadow-soft p-8">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold font-heading text-slate-900 dark:text-white mb-2">
                Invalid Reset Link
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                This password reset link is invalid or has expired. Please request a new one.
              </p>
              <div className="space-y-3">
                <button
                  onClick={handleRequestNewReset}
                  className="w-full px-4 py-2 bg-gradient-to-r from-brand-400 to-brand-500 hover:from-brand-300 hover:to-brand-500 text-white rounded-lg font-medium transition-all"
                >
                  Request New Reset Link
                </button>
                <button
                  onClick={handleBackToLogin}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-midnight-800 transition-colors"
                >
                  Back to Login
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[radial-gradient(circle_at_20%_20%,rgba(241,79,48,.12),transparent_55%),radial-gradient(circle_at_80%_0%,rgba(31,159,132,.12),transparent_50%),linear-gradient(160deg,#f8fafc,#eef2ff_45%,#e0e7ff_90%)] dark:bg-[radial-gradient(circle_at_20%_20%,rgba(241,79,48,.15),transparent_45%),radial-gradient(circle_at_80%_0%,rgba(31,159,132,.12),transparent_40%),linear-gradient(160deg,#070c17,#0f1727_40%,#111936_75%)] text-slate-900 dark:text-slate-100">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white/95 dark:bg-midnight-900/95 backdrop-blur-xl rounded-2xl shadow-xl shadow-slate-200/60 dark:shadow-soft p-8">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-accent-100 dark:bg-accent-900/30 flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-accent-600 dark:text-accent-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold font-heading text-slate-900 dark:text-white mb-2">
                Password Reset Successful
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                Your password has been successfully reset. You can now sign in with your new password.
              </p>
              <button
                onClick={handleBackToLogin}
                className="w-full px-4 py-2 bg-gradient-to-r from-brand-400 to-brand-500 hover:from-brand-300 hover:to-brand-500 text-white rounded-lg font-medium transition-all"
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main reset form
  return (
    <div className="min-h-screen flex items-center justify-center bg-[radial-gradient(circle_at_20%_20%,rgba(241,79,48,.12),transparent_55%),radial-gradient(circle_at_80%_0%,rgba(31,159,132,.12),transparent_50%),linear-gradient(160deg,#f8fafc,#eef2ff_45%,#e0e7ff_90%)] dark:bg-[radial-gradient(circle_at_20%_20%,rgba(241,79,48,.15),transparent_45%),radial-gradient(circle_at_80%_0%,rgba(31,159,132,.12),transparent_40%),linear-gradient(160deg,#070c17,#0f1727_40%,#111936_75%)] text-slate-900 dark:text-slate-100">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white/95 dark:bg-midnight-900/95 backdrop-blur-xl rounded-2xl shadow-xl shadow-slate-200/60 dark:shadow-soft p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-400 via-brand-500 to-brand-600 shadow-glow flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold font-heading text-slate-900 dark:text-white mb-2">
              Set New Password
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              Enter your new password below.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                New Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl shadow-sm
                         placeholder-slate-400 dark:placeholder-slate-500
                         text-slate-900 dark:text-white
                         bg-white dark:bg-midnight-800
                         focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500
                         transition-colors duration-200"
                placeholder="Enter your new password"
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Must be at least 8 characters long
              </p>
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl shadow-sm
                         placeholder-slate-400 dark:placeholder-slate-500
                         text-slate-900 dark:text-white
                         bg-white dark:bg-midnight-800
                         focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500
                         transition-colors duration-200"
                placeholder="Confirm your new password"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl
                       text-sm font-semibold text-white
                       bg-gradient-to-r from-brand-400 to-brand-500 hover:from-brand-300 hover:to-brand-500
                       focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500
                       disabled:from-brand-300 disabled:to-brand-400
                       disabled:cursor-not-allowed
                       transition-all duration-200
                       shadow-lg hover:shadow-xl"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Resetting Password...
                </div>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="text-center mt-6">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Remember your password?{' '}
              <Link
                to="/login"
                className="font-medium text-brand-600 hover:text-brand-500 dark:text-brand-400 dark:hover:text-brand-300 transition-colors"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
